import React, {
  KeyboardEventHandler,
  useEffect,
  useRef,
} from 'react';

import { useAtom, useSetAtom } from 'jotai';
import {
  Controller,
  SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { uiRoutes, usePreviewDeposit } from '@consumer-frontend/nc-api';
import { validateDrawAmount } from '@consumer-frontend/nc-utils';

import { RequestCashAdvanceFormT, useRequestedDrawPreview } from './useRequestedDrawPreview';
import {
  hasPreviewDepositErrorAtom,
  requestedDrawAmountAtom,
  standardErrorHeader,
  standardErrorMessage,
} from '../../store';
import { NcButton } from '../NcButton';
import { NcCard, NcCardHeading } from '../NcCard';
import { NcDepositedAmount } from '../NcDepositedAmount';
import { NcError } from '../NcError';
import { NcForm } from '../NcForm';
import { NcLoadingSpinner } from '../NcLoadingSpinner';
import { NcRequestedDrawAmountInput } from '../NcRequestedDrawAmountInput';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/draw-request/request-cash-advance-card.scss';

export type NcRequestCashAdvanceCardPropsT = {
  cashAdvanceFee?: string;
  hasApiError?: boolean;
  isLoading?: boolean;
  maxDrawAmount?: number;
  minDrawAmount?: number;
};
export type NcRequestCashAdvanceCardT = React.FC<NcRequestCashAdvanceCardPropsT>;

export const NcRequestCashAdvanceCard: NcRequestCashAdvanceCardT = ({
  cashAdvanceFee,
  hasApiError,
  isLoading,
  maxDrawAmount,
  minDrawAmount,
}) => {
  const navigate = useNavigate();
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const [alreadyRequestedDrawAmount, setRequestedDrawAmount] = useAtom(requestedDrawAmountAtom);
  const setHasPreviewDepositError = useSetAtom(hasPreviewDepositErrorAtom);
  const {
    control,
    handleSubmit,
    trigger,
  } = useFormContext<RequestCashAdvanceFormT>();
  const previewAmount = useRequestedDrawPreview({ maxDrawAmount, minDrawAmount });

  const {
    depositedAmount,
    drawFee,
    isLoading: isPreviewDepositLoading,
    error: previewDepositError,
  } = usePreviewDeposit(previewAmount);

  const onEnterKey: KeyboardEventHandler<HTMLInputElement> = (event): void => {
    if (event.key !== 'Enter') { return; }

    event.preventDefault();
    continueButtonRef.current?.focus();
  };

  const onSubmit: SubmitHandler<RequestCashAdvanceFormT> = ({ requestedDrawAmount }) => {
    setRequestedDrawAmount(requestedDrawAmount);
    navigate(uiRoutes.dashboard.requestCashAdvanceReview);
  };

  // Put error in Jotai atom so page knows not to show MPE.
  useEffect(
    () => {
      setHasPreviewDepositError(!!previewDepositError);

      return () => setHasPreviewDepositError(false);
    },
    [previewDepositError, setHasPreviewDepositError],
  );

  return (
    <NcCard className="nc-request-cash-advance-card">
      <NcCardHeading level={1} className="h2">Request Cash Advance</NcCardHeading>

      {isLoading && <NcLoadingSpinner />}

      {hasApiError && !isLoading && (
        <NcError
          className="w-100"
          header={standardErrorHeader}
          message={standardErrorMessage}
        />
      )}

      {!hasApiError && !isLoading && !!maxDrawAmount && (
        <NcForm className="nc-request-cash-advance-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="stackable-or-side-by-side">
            <Controller
              control={control}
              name="requestedDrawAmount"
              render={({ field, fieldState }) => (
                <NcRequestedDrawAmountInput
                  errorMessage={fieldState.error?.message}
                  defaultValue={field.value ?? alreadyRequestedDrawAmount ?? maxDrawAmount}
                  maxDrawAmount={maxDrawAmount}
                  minDrawAmount={minDrawAmount || 0}
                  onBlur={field.onBlur}
                  onKeyDown={onEnterKey}
                  ref={field.ref}
                  updateValue={(value) => {
                    field.onChange(value);
                    // Re-validate explicitly so a suggestion click clears a stale error left from the input.
                    trigger('requestedDrawAmount');
                  }}
                />
              )}
              rules={{
                validate: (value) => validateDrawAmount(value, minDrawAmount, maxDrawAmount),
              }}
            />

            <NcDepositedAmount
              cashAdvanceFee={cashAdvanceFee || ''}
              depositedAmount={depositedAmount}
              drawFee={drawFee}
              isLoading={isPreviewDepositLoading}
              requestedAmount={previewAmount}
            />
          </div>

          <div className="cta-buttons">
            <NcButton
              ref={continueButtonRef}
              type="submit"
              variant="primary"
            >
              Continue to Review
            </NcButton>
          </div>
        </NcForm>
      )}
    </NcCard>
  );
};
